import React, { useState } from "react";

const SubmittalForm = ({
  value,
  onChange,
  onSubmitWithAttachments,
  onCancel,
  creating,
  uploadingAttachments,
  teamOptions = [],
  teamLoading = false,
}) => {
  const form = value || {};

  const [documentFiles, setDocumentFiles] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);

  const handleFieldChange = (field) => (e) => {
    const v = e.target.value;
    onChange({
      ...form,
      [field]: v,
    });
  };

  const handleAssignedToChange = (e) => {
    const v = e.target.value;
    onChange({
      ...form,
      assigned_to_user_id: v ? Number(v) : null,
    });
  };

  const handleDocumentFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setDocumentFiles(files);
  };

  const handlePhotoFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotoFiles(files);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!onSubmitWithAttachments) return;

    onSubmitWithAttachments(
      {
        ...form,
        submit_date: form.submit_date || null,
        required_by_date: form.required_by_date || null,
      },
      {
        documentFiles,
        photoFiles,
      },
    );
  };

  const busy = creating || uploadingAttachments;

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-lg shadow p-4 space-y-4"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            New Submittal
          </div>
          <p className="text-xs text-gray-500">
            Capture contractor submittals with key meta data and assign for
            review.
          </p>
        </div>
        {busy && (
          <div className="text-xs text-gray-500">
            {creating ? "Saving..." : "Uploading attachments..."}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Title */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">Title</label>
          <input
            className="border rounded-md px-3 py-2 text-sm"
            value={form.title || ""}
            onChange={handleFieldChange("title")}
            placeholder="Submittal title"
          />
        </div>

        {/* Type */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">Type</label>
          <input
            className="border rounded-md px-3 py-2 text-sm"
            value={form.submittal_type || ""}
            onChange={handleFieldChange("submittal_type")}
            placeholder="e.g. Mix Design, Shop Drawing"
          />
        </div>

        {/* Contractor */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">
            Contractor
          </label>
          <input
            className="border rounded-md px-3 py-2 text-sm"
            value={form.contractor || ""}
            onChange={handleFieldChange("contractor")}
            placeholder="Contractor name"
          />
        </div>

        {/* Submit Date */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">
            Submit Date
          </label>
          <input
            type="date"
            className="border rounded-md px-3 py-2 text-sm"
            value={form.submit_date || ""}
            onChange={handleFieldChange("submit_date")}
          />
        </div>

        {/* Required By */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">
            Required By
          </label>
          <input
            type="date"
            className="border rounded-md px-3 py-2 text-sm"
            value={form.required_by_date || ""}
            onChange={handleFieldChange("required_by_date")}
          />
        </div>

        {/* Spec Section */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">
            Specification Section
          </label>
          <input
            className="border rounded-md px-3 py-2 text-sm"
            value={form.specification_section || ""}
            onChange={handleFieldChange("specification_section")}
            placeholder="e.g. 03300"
          />
        </div>

        {/* Assigned To */}
        <div className="flex flex-col gap-1 md:col-span-3">
          <label className="text-xs font-medium text-gray-700">Assign To</label>
          <select
            className="border rounded-md px-3 py-2 text-sm"
            value={form.assigned_to_user_id || ""}
            onChange={handleAssignedToChange}
          >
            <option value="">
              {teamLoading ? "Loading team..." : "Unassigned"}
            </option>
            {teamOptions.map((opt) => (
              <option key={opt.key} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-gray-500">
            Contractor can assign this submittal to the QA/QC Manager, PM, or
            other designated reviewer.
          </p>
        </div>
      </div>

      {/* Notes */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-700">
          Notes / Description
        </label>
        <textarea
          rows={3}
          className="border rounded-md px-3 py-2 text-sm"
          value={form.notes || ""}
          onChange={handleFieldChange("notes")}
          placeholder="Describe the submittal, included documents, key details, etc."
        />
      </div>

      {/* Attachments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">
            Attach Documents
          </label>
          <input
            type="file"
            multiple
            onChange={handleDocumentFilesChange}
            className="text-xs"
          />
          <p className="text-[11px] text-gray-500">
            Upload PDF specifications, mix designs, shop drawings, etc.
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">
            Attach Photos
          </label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handlePhotoFilesChange}
            className="text-xs"
          />
          <p className="text-[11px] text-gray-500">
            Upload supporting photos if needed.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={busy}
          className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
        >
          {busy ? "Saving..." : "Save Submittal"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-md bg-gray-100 text-gray-800 text-sm font-medium hover:bg-gray-200"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default SubmittalForm;
